import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import PostCanvas from '../../social/components/PostCanvas'
import LazyPreview from '../../social/components/studio/LazyPreview'
import InstagramFrame from '../../social/components/studio/InstagramFrame'
import { postTheme } from '../../social/render/render'
import { buildCaption } from '../../social/data/captions'
import { EmailFrame, EmailThumb } from '../../email-composer/components/EmailHtml'
import { hapticTick } from '../../../../../_utils/haptics'
import { ChannelIcon } from '../../plans/components/PlanCard'
import { CHANNEL_NAMES } from '../../plans/data/planner'
import { analyseSmsExpanded } from '../../sms-composer/smsUtils'
import { previewText } from '../data/preview'
import { ROLES } from '../data/story'

// The width of a box, kept up to date
const useWidth = () => {
  const ref = useRef(null)
  const [w, setW] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => setW(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

// A post drawn at exactly this width once it's near the screen (a Story is
// 9:16, a post 4:5), for thumbnails
export const LazyCanvas = ({ post, kit, width }) => {
  const ref = useRef(null)
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined')
  useEffect(() => {
    if (seen || !ref.current) return undefined
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setSeen(true); io.disconnect() }
    }, { rootMargin: '200px' })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [seen])
  const story = post.format === 'story'
  const height = Math.round(width * (story ? 16 / 9 : 5 / 4))
  return (
    <span ref={ref} className="cst-canvas" style={{ width, height }}>
      {seen && <PostCanvas post={post} theme={postTheme(post, { kit })} format={story ? 'story' : 'post'} width={width} thumb />}
    </span>
  )
}

// Links in a text, drawn the way a phone draws them
const LINK = /^(https?:\/\/|www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i
const Linked = ({ text }) => String(text || '').split(/(\s+)/).map((t, i) => (LINK.test(t) && /[./]/.test(t) ? <span key={i} className="cst-link">{t}</span> : t))

// What an item says in a line, for rows and cards
export const summaryOf = (it, { salonName = '', links = {} } = {}) => {
  if (it.post) {
    const c = it.post.content || {}
    return String(c.headline || c.quote || c.name || c.title || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim()
  }
  if (it.email) return previewText(it.email.subject, { salonName, links })
  return previewText(String(it.message || '').split('\n')[0], { salonName, links })
}

// A small picture of an item, for the days down the side
export const ItemThumb = ({ item, kit }) => {
  if (item.post) {
    const story = item.post.format === 'story'
    return (
      <span className={`cst-thumb is-${story ? 'story' : 'post'}`} aria-hidden>
        <LazyCanvas post={item.post} kit={kit} width={story ? 28 : 40} />
      </span>
    )
  }
  return <span className={`cst-thumb is-tile is-${item.channel}`} aria-hidden><ChannelIcon channel={item.channel} size={16} /></span>
}

// A post in the feed, or a Story, as Instagram shows it
export const PostPreview = ({ post, kit, salonName, town }) => {
  const [ref, w] = useWidth()
  const story = post.format === 'story'
  const width = Math.floor(Math.min(w, story ? 300 : 440))
  const caption = useMemo(() => post.caption || buildCaption(post, { voice: 'warm', salon: salonName, town }), [post, salonName, town])
  return (
    <div ref={ref} className={`cst-stage is-${story ? 'story' : 'post'}`}>
      {width > 0 && (
        <InstagramFrame format={story ? 'story' : 'post'} width={width} name={kit.name} logo={kit.logo} caption={caption}>
          <PostCanvas post={post} theme={postTheme(post, { kit })} format={story ? 'story' : 'post'} width={width} />
        </InstagramFrame>
      )}
    </div>
  )
}

// An email as it lands: the inbox line, then the whole email at phone or
// computer width
export const EmailPreview = ({ built, salonName, links }) => {
  const [size, setSize] = useState('phone')
  if (!built) return null
  return (
    <div className="cst-email">
      <div className="cst-seg" role="radiogroup" aria-label="Screen">
        {[{ id: 'phone', label: 'Phone' }, { id: 'computer', label: 'Computer' }].map(o => (
          <button key={o.id} type="button" role="radio" aria-checked={size === o.id} className={`cst-seg-btn${size === o.id ? ' is-on' : ''}`} onClick={() => { if (size !== o.id) { hapticTick(); setSize(o.id) } }}>{o.label}</button>
        ))}
      </div>
      <div className={`cst-mail is-${size}`}>
        <div className="cst-inbox">
          <span className="cst-inbox-from">{salonName || 'Your salon'}</span>
          <span className="cst-inbox-subject">{previewText(built.subject, { salonName, links })}</span>
          {built.preheader ? <span className="cst-inbox-pre">{previewText(built.preheader, { salonName, links })}</span> : null}
        </div>
        {built.html ? <EmailFrame html={built.html} width={size === 'phone' ? 390 : 640} /> : <p className="cst-muted">The preview couldn’t be drawn.</p>}
      </div>
    </div>
  )
}

// A text on a phone, with the real name, link and opt-out
export const TextPreview = ({ message, salonName, links }) => {
  const a = analyseSmsExpanded(message, salonName)
  return (
    <div className="cst-phone">
      <div className="cst-phone-top">
        <span className="cst-phone-avatar" aria-hidden>{String(salonName || 'B').trim().charAt(0).toUpperCase()}</span>
        <b>{salonName || 'Your salon'}</b>
        <small>Text message</small>
      </div>
      <div className="cst-phone-body">
        <p className="cst-bubble"><Linked text={previewText(message, { salonName, links })} /></p>
      </div>
      <p className="cst-phone-note">{a.segments === 1 ? 'Fits in one text.' : `About ${a.segments} texts long, so it costs ${a.segments} times as much.`}</p>
    </div>
  )
}

export const ItemPreview = ({ item, kit, salonName, town, built, links }) => {
  if (!item) return null
  if (item.post) return <PostPreview post={item.post} kit={kit} salonName={salonName} town={town} />
  if (item.channel === 'email') return <EmailPreview built={built} salonName={salonName} links={links} />
  return <TextPreview message={item.message} salonName={salonName} links={links} />
}

// Every item in order as a strip of pictures, with its day and its job: the
// fastest way to see whether a campaign tells a story
export const Storyboard = ({ items, kit, emails, salonName, links, selected, onPick }) => {
  const on = items.filter(i => i.on).sort((a, b) => new Date(a.at) - new Date(b.at))
  if (!on.length) return <p className="cst-muted cst-empty-line">Nothing switched on yet.</p>
  return (
    <ol className="cst-board" aria-label="Storyboard">
      {on.map(it => (
        <li key={it.id}>
          <button type="button" className={`cst-board-card${selected === it.id ? ' is-on' : ''}`} onClick={() => onPick(it.id)} aria-label={`${CHANNEL_NAMES[it.channel]}, ${moment(it.at).format('dddd D MMMM')}`}>
            <span className={`cst-board-art is-${it.post ? (it.post.format === 'story' ? 'story' : 'post') : it.channel}`}>
              {it.post && <LazyCanvas post={it.post} kit={kit} width={it.post.format === 'story' ? 101 : 132} />}
              {it.channel === 'email' && emails[it.id] && emails[it.id].html && <EmailThumb html={emails[it.id].html} width={600} height={820} />}
              {it.channel === 'sms' && <span className="cst-board-text">{previewText(it.message, { salonName, links })}</span>}
            </span>
            <span className="cst-board-meta">
              <b>{ROLES[it.role] || CHANNEL_NAMES[it.channel]}</b>
              <small><ChannelIcon channel={it.channel} size={11} />{moment(it.at).format('ddd D MMM')}</small>
            </span>
          </button>
        </li>
      ))}
    </ol>
  )
}

// The salon's Instagram grid with this campaign's posts in date order among
// their own, three across, the way someone scrolling their profile sees it
export const GridPreview = ({ items, kit, posts = [], campaignId = null, selected, onPick }) => {
  const ours = items.filter(i => i.on && i.channel === 'post').map(i => ({ key: i.id, id: i.id, post: i.post, at: i.at, ours: true }))
  const theirs = (posts || [])
    .filter(p => p && (p.format || 'post') === 'post' && (p.status === 'posted' || p.status === 'planned') && (!campaignId || p.campaignId !== campaignId))
    .map(p => ({ key: p.id, post: p, at: p.postedAt || p.plannedAt || p.updatedAt || p.createdAt, ours: false }))
  const all = [...ours, ...theirs].filter(x => x.at).sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 12)
  const pad = Math.max(0, (3 - (all.length % 3)) % 3) + (all.length < 6 ? 3 : 0)
  return (
    <div className="cst-grid-wrap">
      <div className="cst-grid" role="list" aria-label="Your grid with this campaign’s posts">
        {all.map(x => (
          <button key={x.key} type="button" role="listitem" disabled={!x.ours} className={`cst-grid-cell${x.ours ? ' is-ours' : ''}${x.ours && selected === x.id ? ' is-on' : ''}`} onClick={() => x.ours && onPick(x.id)} aria-label={x.ours ? `This campaign, ${moment(x.at).format('D MMMM')}` : 'One of your posts'}>
            <LazyPreview post={x.post} theme={postTheme(x.post, { kit })} format="post" />
            {x.ours && <span className="cst-grid-day">{moment(x.at).format('D MMM')}</span>}
          </button>
        ))}
        {Array.from({ length: pad }, (_, i) => <span key={`pad-${i}`} className="cst-grid-cell is-empty" aria-hidden />)}
      </div>
      <p className="cst-muted">{theirs.length ? 'Newest first. This campaign’s posts have a date on them; the rest are your own posts from Social.' : 'Newest first. Your posts from Social fill in below as you share them.'}</p>
    </div>
  )
}

// A post as a thumbnail anywhere else (start page cards)
export const PostThumb = ({ post, kit, width = 72 }) => <LazyCanvas post={post} kit={kit} width={width} />
